<script setup lang="ts">
import { yieldToMain } from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { getSearchParams } from '@@/bits/location'
import { defineAsyncComponent } from '@@/bits/vue'
import OzPadletLogo, {
  OzPadletLogoColorScheme,
  OzPadletLogoSizePreset,
} from '@@/library/v4/components/OzPadletLogo.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { BreakPoints } from '@@/pinia/window_size'
import { useWhiteboardFetchInitialState } from '@@/vuecomposables/whiteboard_fetch_initial_state'
import { once } from 'es-toolkit'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

const SurfaceContainerHeaderPad = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerHeaderPad.vue'))
const WhiteboardPanels = defineAsyncComponent(() => import('@@/vuecomponents/WhiteboardPanels.vue'))

// Started at module eval rather than at render so the canvas — tldraw, React, ProseMirror — streams
// in beside the shell instead of after it, while staying off the entry's load-blocking graph.
// Vite's `__vitePreload` fans this chunk's whole dependency set out in one round trip; the
// entrypoint's static graph gets no preload hints at all (see `skip_preload_tags` in
// surface/show/_script.html.erb), so a static import here is the slower of the two.
const whiteboardContainerMainModule = import('@@/vuecomponents/WhiteboardContainerMain.vue')
// The loader below re-awaits the same promise, so a failure still reaches defineAsyncComponent's
// onError (and asset-load recovery). This catch only stops a rejection that lands before first
// render from also firing as an unhandled rejection.
whiteboardContainerMainModule.catch(() => {})
const WhiteboardContainerMain = defineAsyncComponent(async () => await whiteboardContainerMainModule)

const whiteboardContainerEl = ref<HTMLElement | null>(null)
const whiteboardStore = useWhiteboardStore()
const surfaceStore = useSurfaceStore()
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const surfacePostsStore = useSurfacePostsStore()

const headerBackgroundTop = ref(0)

const isSidePanelDesktop = computed(
  () => surfaceStore.hasSidePanelOutsideSurface && !surfaceContainerSizeStore.isContainerSmallerThanTabletPortrait,
)
const isSidePanelDesktopRounded = computed(() => surfaceStore.isSidePanelRounded && isSidePanelDesktop.value)
const headerHeight = computed(() => surfaceStore.headerHeight)
const headerColor = computed(() => surfaceStore.headerColor)
const isDesktopLayout = computed(() => surfaceStore.isDesktopLayout)
const { isContainerSmallerThanTabletLandscape } = storeToRefs(surfaceContainerSizeStore)
const isReadyToMountWhiteboardContainerMain = ref(false)
const isReadyToMountWhiteboardPanels = ref(false)

/**
 * LIFECYCLE HOOKS + UTILITIES
 */

onMounted(async () => {
  useWhiteboardFetchInitialState().fetchInitialState()
  surfacePostsStore.fetchPosts() // whiteboard walls don't have posts are used to store attachments
  surfaceContainerSizeStore.observeSurfaceContainerSize(whiteboardContainerEl.value as HTMLElement)
  setTimeout(() => {
    import('@@/pinia/content_picker_container_size').then(({ useContentPickerContainerSizeStore }) => {
      useContentPickerContainerSizeStore().observeContainerSize(whiteboardContainerEl.value as HTMLElement)
    })
  })

  if (!whiteboardStore.xWhiteboardHeader) {
    surfaceStore.resizeHeader({ height: 0 })
  }

  await yieldToMain()
  isReadyToMountWhiteboardContainerMain.value = true

  await yieldToMain()
  isReadyToMountWhiteboardPanels.value = true
})

/**
 * WHITEBOARD SUPPORT
 */
const isWhiteboardSupported = ref(true)

function checkAndSetWhiteboardSupport() {
  if (getSearchParams().dev) {
    isWhiteboardSupported.value = true
    return
  }
  isWhiteboardSupported.value = true
}

const checkAndSetWhiteboardSupportOnce = once(checkAndSetWhiteboardSupport)
onMounted(() => {
  checkAndSetWhiteboardSupportOnce()
})

/**
 * Allows the server to determine if a single browser env has multiple connections open, i.e. multiple tabs/windows open.
 *
 * See serverless/whiteboard-server/src/server.ts for the full context of why this is needed.
 */

function generateUUID(): string {
  let d = new Date().getTime() // Timestamp
  let d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0 // Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16 // random number between 0 and 16
    if (d > 0) {
      // Use timestamp until depleted
      r = (d + r) % 16 | 0
      d = Math.floor(d / 16)
    } else {
      // Use microseconds since page-load if supported
      r = (d2 + r) % 16 | 0
      d2 = Math.floor(d2 / 16)
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

onMounted(() => {
  const whiteboardClientId = localStorage.getItem('whiteboardClientId')
  if (!whiteboardClientId) {
    localStorage.setItem('whiteboardClientId', generateUUID())
  }
})
</script>

<template>
  <main
    v-if="isWhiteboardSupported"
    ref="whiteboardContainerEl"
    :class="[
      'flex',
      isContainerSmallerThanTabletLandscape && 'flex-col',
      'w-screen h-vh100',
      '@container/surface-container',
    ]"
  >
    <SurfaceContainerHeaderPad
      v-if="isDesktopLayout"
      :height="headerHeight"
      :color="headerColor"
      :top="headerBackgroundTop"
    />
    <WhiteboardContainerMain v-if="isReadyToMountWhiteboardContainerMain" />
    <WhiteboardPanels v-if="isReadyToMountWhiteboardPanels" />
    <template v-if="isSidePanelDesktopRounded">
      <div class="h-4 w-4 end-0 top-0 absolute corner-top"></div>
      <div class="h-4 w-4 end-0 bottom-0 absolute corner-bottom"></div>
    </template>
  </main>
  <main
    v-else
    ref="whiteboardContainerEl"
    :class="{
      flex: true,
      'flex-col': true,

      'h-full': true,
      'w-full': true,
      'bg-common-ui-lightened-bg': surfaceStore.backgroundLuminance === 'light',
      'bg-common-ui-darkened-bg': surfaceStore.backgroundLuminance === 'dark',
    }"
  >
    <div v-if="whiteboardStore.xWhiteboardHeader" class="mt-4.5 ms-5 flex flex-row items-center">
      <OzPadletLogo
        :href="surfaceStore.homeUrl"
        :dark-mode="surfaceStore.backgroundLuminance === 'dark'"
        :size-preset="OzPadletLogoSizePreset.W48px"
        :color-scheme="OzPadletLogoColorScheme.Text300"
        data-testid="padletLogo"
      />
    </div>
    <div
      :class="{
        grow: true,
        flex: true,
        'flex-col': true,
        'justify-center': true,
        'items-center': true,
        'text-heading-4': true,
        'font-semibold': true,
        'text-center': true,
        'py-8': true,
        'text-dark-text-100': surfaceStore.backgroundLuminance === 'light',
        'text-light-text-100': surfaceStore.backgroundLuminance === 'dark',
      }"
    >
      <span v-if="surfaceContainerSizeStore.surfaceContainerHeight >= BreakPoints.TabletLandscape">
        {{ __('Please rotate your device to use this sandbox.') }}
      </span>
      <span v-else>
        {{ __('Mobile support is coming soon!') }}
        <br />
        {{ __('You can open this sandbox on your computer.') }}
      </span>
    </div>
  </main>
</template>

<style scoped lang="scss">
@import '@@/styles/3/modules/all';

.corner-top {
  background: radial-gradient(circle at bottom left, transparent 16px, black 16px);
  z-index: $sidepanel-zindex;

  [dir='rtl'] & {
    background: radial-gradient(circle at bottom right, transparent 16px, black 16px);
  }
}

.corner-bottom {
  background: radial-gradient(circle at top left, transparent 16px, black 16px);
  z-index: $sidepanel-zindex;

  [dir='rtl'] & {
    background: radial-gradient(circle at top right, transparent 16px, black 16px);
  }
}
</style>
